import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";
import { formatRating, getCategoryName } from "@/utils/movie";

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((v) => (v + 1) % items.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [paused, items.length]);

  if (!items.length) return null;

  const go = (next: number) => setIndex((next + items.length) % items.length);

  return (
    <section
      className="relative h-[460px] w-full overflow-hidden md:h-[560px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {items.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title} 焦点推荐`}
            title={`${movie.title} - 热门推荐`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
        </div>
      ))}

      <div className="relative z-10 flex h-full w-full items-center px-4 md:px-8">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-400/60 bg-primary-500/20 px-3 py-1 text-xs text-accent-200 backdrop-blur">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-fire-fill text-primary-300" />
            </span>
            焦点推荐 · {getCategoryName(items[index].category)}
          </span>
          <h1 className="mt-4 font-heading text-3xl leading-tight text-background-50 md:text-5xl">
            {items[index].title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
            <span className="flex items-center gap-1 font-medium text-accent-300">
              <i className="ri-star-fill text-xs" />
              {formatRating(items[index].rating)} 分
            </span>
            <span>{items[index].year}</span>
            <span>{items[index].region}</span>
            <span>{items[index].genres.join(" / ")}</span>
          </div>
          <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
            {items[index].summary}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${items[index].id}`}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-play-circle-line" />
              </span>
              查看详情
            </Link>
            <Link
              to={`/category/${items[index].category}`}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/50 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
            >
              更多{getCategoryName(items[index].category)}
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {items.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张推荐`}
            onClick={() => setIndex(i)}
            className={`h-2 cursor-pointer rounded-full transition-all ${
              i === index ? "w-6 bg-primary-400" : "w-2 bg-background-50/60"
            }`}
          />
        ))}
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="absolute left-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/35 text-background-50 backdrop-blur transition-colors hover:bg-black/55 md:flex"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="absolute right-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/35 text-background-50 backdrop-blur transition-colors hover:bg-black/55 md:flex"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>
    </section>
  );
}